import { ErrorBoundary, FlexBox, Spinner } from '@dixa/lib-shared-habla';
import { AgentInterfaceSuggestionsProps } from 'lib-knowledge-types';
import { lazy, Suspense } from 'react';

import { CardMoveHandle } from 'components/conversation/components/sidebar/components/cardMoveHandle/cardMoveHandle';
import { VisibilityToggle } from 'components/conversation/components/sidebar/components/visibilityToggle/visibilityToggle';
import { actions as personalActions } from 'components/settings/actions/personal';
import { useNotifications } from 'hooks/useNotifications';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useCopilotTrial } from 'utils/useCopilotTrial';
import { UseOrderedDragDropOptions } from 'utils/useOrderedDragDrop';

import { KnowledgeErrorPlaceholder } from '../../../elevio/src/components/KnowledgeErrorPlaceholder';

import { knowledgeSelectors } from './knowledgeSelectors';
import styles from './knowledgeSuggestions.module.scss';

const Suggestions = lazy(() => import('lib-knowledge-suggestions'));

type KnowledgeSuggestionsProps = Pick<
  AgentInterfaceSuggestionsProps,
  'isMinimized'
> &
  Pick<UseOrderedDragDropOptions, 'type' | 'index' | 'onMove'>;

export function KnowledgeSuggestions({
  isMinimized,
  index,
  type,
  onMove,
}: KnowledgeSuggestionsProps): JSX.Element | null {
  const conversationId = useAppSelector(knowledgeSelectors.conversation.id);
  const conversationTags = useAppSelector(knowledgeSelectors.conversation.tags);
  const conversationText = useAppSelector(knowledgeSelectors.conversation.text);
  const queueId = useAppSelector(knowledgeSelectors.conversation.queueId);

  const notify = useNotifications();
  const dispatch = useAppDispatch();
  const { hasCopilot } = useCopilotTrial();

  const { smartArticleCreate: hasSmartArticleCreate } = useFeatureFlag([
    'smart-article-create',
  ]);

  const knowledgeCardOpen = useAppSelector(
    (state) => state.settings.personal.knowledgeCardOpen,
  );

  function setKnowledgeCardOpen(newValue: boolean) {
    dispatch(
      personalActions.personalToggleRememberKnowledgeCardOpen({
        knowledgeCardOpen: newValue,
      }),
    );
  }

  if (!conversationId) return null;

  return (
    <ErrorBoundary fallback={<KnowledgeErrorPlaceholder />}>
      <Suspense
        fallback={
          <FlexBox className={styles.spinner} justifyContent="center">
            <Spinner />
          </FlexBox>
        }
      >
        <Suggestions
          conversationId={conversationId}
          conversationTags={conversationTags}
          conversationText={conversationText}
          isMinimized={isMinimized}
          isOpen={knowledgeCardOpen}
          setIsOpen={setKnowledgeCardOpen}
          queueId={queueId}
          notify={notify}
          featureFlags={{
            hasSmartReply: hasCopilot,
            hasSmartArticleCreate,
          }}
          getDragHandle={(row) => (
            <CardMoveHandle
              index={index}
              target={row}
              type={type}
              onMove={onMove}
            />
          )}
          slotAfterTitle={<VisibilityToggle cardId="knowledge-suggestions" />}
        />
      </Suspense>
    </ErrorBoundary>
  );
}
